import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { BimActKc2GroupTypeEnum, type BimActKc2ListItemData } from 'gql/graphql';
import { usePageFilterSaver, withDefaultTransformer } from 'hooks/usePageFilterSaver';
import type { TGroupedData, TOrderState } from 'pages/Project/Common/ActsKC2ListContainer/helpers';
import { getGroupByType, getOrderValues, groupIdGetter } from 'pages/Project/Common/ActsKC2ListContainer/helpers';

import type { SortChangedEvent } from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getBimWorkOrderActsKc2List } from '../BimWorkOrderActsKc2ListQueries';

import type { TFilterFieldsType } from './useBimActsKc2ListColumns';

const getFilterWhere = (
    fieldName: TFilterFieldsType['fieldName'],
    valuesArr: TFilterFieldsType['checkedValues'],
    prevState: TFilterWhere | undefined,
) => {
    if (!valuesArr.length && prevState?.and[fieldName]) {
        return { [fieldName]: undefined };
    }

    if (fieldName === 'chapter' || fieldName === 'costGroup' || fieldName === 'actDate') {
        return {
            [fieldName]: {
                in: valuesArr,
            },
        };
    }

    if (fieldName === 'constructionType.name') {
        const fieldNameSplit = fieldName.split('.')[0];

        if (!valuesArr.length && prevState?.and?.[fieldNameSplit]) {
            return { [fieldNameSplit]: undefined };
        }

        return {
            [fieldNameSplit]: {
                name: {
                    or: valuesArr.map((item: string) => ({
                        eq: item,
                    })),
                },
            },
        };
    }

    return {
        [fieldName]: {
            or: valuesArr.map((item: string) => ({
                eq: item,
            })),
        },
    };
};

type TFilterWhere = Record<'and', any>;

export type TGroupedState = { [key: string]: boolean };
export const useGetBimActsKc2List = ({
    workOrderId,
    groupType,
}: {
    workOrderId: number;
    groupType: BimActKc2GroupTypeEnum | null;
}) => {
    const dispatch = useDispatch();
    const [groupState, setGroupState] = useState<TGroupedState>({});

    const [filters, setFilters] = useState<TFilterWhere | undefined>(undefined);
    const [order, setOrder] = useState<TOrderState | TOrderState[] | undefined>(undefined);

    const changeFilterHandler = useCallback<(props: TFilterFieldsType) => void>(({ checkedValues, fieldName }) => {
        setFilters((prevValues) => ({
            and: {
                ...prevValues?.and,
                ...getFilterWhere(fieldName, checkedValues, prevValues),
            },
        }));
    }, []);

    const { canSendRequest } = usePageFilterSaver(
        useMemo(
            () => ({
                filterSetter: withDefaultTransformer(changeFilterHandler as any, 'bimActList', () => false),
                withFakeSetter: true,
            }),
            // hack: после обновления типа группировки/сортировки хотим обновить функцию, для обновления canSendRequest
            //  eslint-disable-next-line react-hooks/exhaustive-deps
            [changeFilterHandler, groupType, order],
        ),
    );

    const changeOrderHandler = useCallback(
        (params: SortChangedEvent<TGroupedData<BimActKc2ListItemData, BimActKc2GroupTypeEnum>>) => {
            const columnsState = params?.api?.getColumnState();

            const { sort, colId } = columnsState?.find((item) => item.sort !== null) ?? {};

            setOrder((prevState) => getOrderValues(sort, colId, groupType, prevState));
        },
        [groupType],
    );

    const changeGroupState = useCallback(
        (id) => () => {
            setGroupState((prevState) => ({ ...prevState, [id]: !prevState[id] ?? false }));
        },
        [],
    );

    const variables = useMemo(
        () => ({
            groupType,
            workOrderId,
            where: filters,
            order,
        }),
        [order, filters, groupType, workOrderId],
    );

    const [listData, getListStatus, , getListRequest] = useRequest({
        requestId: 'bimActsKc2List',
        query: getBimWorkOrderActsKc2List,
        variables,
        withEffect: !!workOrderId && canSendRequest,
    });

    const isLoading = getListStatus === RequestStatus.FETCHING;

    const groupedItems = useMemo<TGroupedData<BimActKc2ListItemData, BimActKc2GroupTypeEnum>[]>(() => {
        if (!isLoading) {
            if (groupType) {
                return getGroupByType(listData?.bimWorkOrderActsKC2?.items ?? [], groupType);
            }

            return listData?.bimWorkOrderActsKC2?.items ?? [];
        }

        return [];
    }, [groupType, isLoading, listData?.bimWorkOrderActsKC2?.items]);

    useEffect(() => {
        if (groupedItems) {
            const groupIds: TGroupedState = {};

            groupedItems.forEach((item) => {
                if ('isGroup' in item && item.isGroup) {
                    groupIds[item.groupId] = true;
                }
            });

            setGroupState(groupIds);
        }
    }, [groupedItems]);

    const actualGroupedItems = useMemo(
        () =>
            groupedItems.filter((item) => {
                if (!('isGroup' in item) && groupType) {
                    return groupState[groupIdGetter(item, groupType)];
                }

                return true;
            }),
        [groupState, groupType, groupedItems],
    );

    const fetchedDataCount = useMemo(
        () =>
            groupType === BimActKc2GroupTypeEnum.Custom
                ? groupedItems?.filter((i) => !i.isGroup).length
                : listData?.bimWorkOrderActsKC2?.items?.length ?? 0,
        [groupType, groupedItems, listData?.bimWorkOrderActsKC2?.items?.length],
    );
    const totalItemsCount = useMemo(
        () =>
            groupType === BimActKc2GroupTypeEnum.Custom
                ? groupedItems?.filter((i) => !i.isGroup).length
                : listData?.bimWorkOrderActsKC2?.totalCount ?? 0,
        [groupType, groupedItems, listData?.bimWorkOrderActsKC2?.totalCount],
    );

    const refreshActListData = useCallback(() => {
        dispatch(getListRequest(variables));
    }, [dispatch, getListRequest, variables]);

    return {
        actKc2List: actualGroupedItems,
        totalItemsCount,
        fetchedDataCount,
        refreshActListData,
        isLoading,
        groupState,
        changeGroupState,
        changeFilterHandler,
        changeOrderHandler,
    };
};
